import { getAgentDefinition } from '@tunasong/plugin-lib/agent-definitions'
import type { Agent, AgentRole, Persisted } from '@tunasong/schemas'

/**
 * Role-keyed defaults for the agent header and empty chat. Used for agents with no definition — we still
 * want to give the user a one-line "who am I talking to" cue and a few starter prompts rather than a blank
 * surface. An agent from a definition takes its header line from the definition's `tagline` instead.
 */
interface RoleDefaults {
  /** One-line "what does this agent do" cue. */
  roleLine: string
  /** Up to 3 starter prompts surfaced as chips. */
  prompts: string[]
}

// Prompts curated by Bowie (music-theory consultant) for breadth — one
// harmony angle, one lyric/prosody angle, one performance angle for
// song-agent; album-level concerns for project-agent; content-neutral
// for folder + generic since those may not be musical at all.
const roleDefaults: Record<AgentRole, RoleDefaults> = {
  'song-agent': {
    roleLine: 'Works with you on this song.',
    prompts: [
      'Suggest a lifted bridge in a new key',
      "Tighten the second verse lyric to match verse 1's meter",
      'Propose a feel and tempo for a live arrangement',
    ],
  },
  'project-agent': {
    roleLine: "Helps you plan and connect this project's songs.",
    prompts: [
      'Sequence these songs into a coherent set list',
      'Find a unifying key or modal centre across the project',
      'Spot lyrical or thematic threads I can lean into',
    ],
  },
  'folder-agent': {
    roleLine: "Helps you find and organise what's in this folder.",
    prompts: [
      "Summarise what's in this folder",
      'Find related pages I should link together',
      'Draft an outline from these notes',
    ],
  },
  generic: {
    roleLine: "Helps with whatever you're working on.",
    prompts: ['What can you help me with here?', 'Summarise the current page', 'Suggest next steps for this work'],
  },
}

/**
 * Role-keyed default colors. Joni's choices for the material palette:
 *  - song-agent → blue (core, signals "this is central")
 *  - project-agent → purple (organizational / planning)
 *  - folder-agent → grey (content-neutral)
 *  - generic → green (fresh / helpful default)
 *
 * Names match `@tunasong/ui-lib`'s `getColorCSS` registry. Unknown roles
 * fall back to `generic`'s green at resolve time.
 */
const roleDefaultColors: Record<AgentRole, string> = {
  'song-agent': 'blue',
  'project-agent': 'purple',
  'folder-agent': 'grey',
  generic: 'green',
}

/**
 * Role-keyed default icon registry keys. Bowie picked the semantic concept
 * per role; the concrete registry key matches the closest neighbour in
 * `@tunasong/icons`:
 *  - song-agent → MusicNote (treble-clef-ish; the registry alias for Music)
 *  - project-agent → LibraryMusic (collection-of-songs; closest semantic
 *    neighbour to the spec's "folder-with-star / briefcase" — the registry
 *    has no Work or BookmarksOutlined)
 *  - folder-agent → Folder (plain folder; literal match)
 *  - generic → SmartButton (the registry's Sparkles alias; closest to the
 *    spec's "sparkle / robot" — registry has no AutoAwesome or SmartToy)
 *
 * Unknown roles fall back to `generic`'s SmartButton at resolve time.
 */
const roleDefaultIcons: Record<AgentRole, string> = {
  'song-agent': 'MusicNote',
  'project-agent': 'LibraryMusic',
  'folder-agent': 'Folder',
  generic: 'SmartButton',
}

const trim = (s: string | undefined): string => s?.trim() ?? ''

/**
 * The agent's definition as the loaded plugins declare it now, not as it was when the agent was created:
 * the header line, icon and starter prompts follow a definition's updates (Notion "AgentDefinition fields
 * baked into Agent entity"). Undefined for an agent with no definition, or one whose plugin isn't loaded.
 */
const definitionOf = (agent: Pick<Agent, 'definitionId'>) =>
  agent.definitionId ? getAgentDefinition(agent.definitionId) : undefined

/**
 * Resolve the header line for an agent: what it does for the band, one line.
 *
 * - An agent from a definition shows that definition's `tagline`, read live, so a changed tagline reaches
 *   every existing agent. No tagline shows nothing (Joni).
 * - An agent with no definition (or one whose plugin isn't loaded) shows the role-keyed default.
 * - Never `agent.roleLine`: it is model-facing, and agents added from the palette before it stopped being
 *   copied carry their definition's whole system prompt there, which this line used to show (and the
 *   composer's `aria-describedby` used to read aloud).
 */
export const resolveDefaultRoleLine = (agent: Persisted<Agent> | Agent): string => {
  const definition = definitionOf(agent)
  if (definition) {
    return trim(definition.tagline)
  }
  return roleDefaults[agent.role]?.roleLine ?? ''
}

/**
 * Resolve up to 3 starter prompts. Priority order:
 * 1. agent.examplePrompts (whitespace-only entries dropped)
 * 2. the live definition's `examplePrompts`
 * 3. role-keyed defaults
 * 4. empty array (unknown role + nothing else seeded)
 */
export const resolveDefaultPrompts = (agent: Persisted<Agent> | Agent): string[] => {
  const explicit = (agent.examplePrompts ?? []).map(trim).filter(p => p.length > 0)
  if (explicit.length > 0) {
    return explicit.slice(0, 3)
  }
  // Read live from the definition, so editing a plugin's prompts reaches every existing agent
  const byDefinition = (definitionOf(agent)?.examplePrompts ?? []).map(trim).filter(p => p.length > 0)
  if (byDefinition.length > 0) {
    return byDefinition.slice(0, 3)
  }
  const byRole = roleDefaults[agent.role]?.prompts
  if (byRole && byRole.length > 0) {
    return byRole.slice(0, 3)
  }
  return []
}

/**
 * Resolve the agent's display color. Order:
 *  1. agent.color (trimmed) when non-empty
 *  2. role-keyed default
 *  3. generic's default ("green") when the role is outside the schema enum
 *
 * Returns a plain string — the material color name. The consumer hands it
 * to `getColorCSS()` (in `@tunasong/ui-lib`) which gracefully resolves
 * unknown names to `inherit`.
 */
export const resolveDefaultColor = (agent: Persisted<Agent> | Agent): string => {
  const explicit = trim(agent.color)
  if (explicit.length > 0) {
    return explicit
  }
  return roleDefaultColors[agent.role] ?? roleDefaultColors.generic
}

/**
 * Resolve the agent's display icon registry key. Same fallback chain as
 * `resolveDefaultColor`. The consumer looks the key up in `@tunasong/icons`'
 * registry; unknown keys fall back to a sensible default at the consumer.
 */
export const resolveDefaultIconKey = (agent: Persisted<Agent> | Agent): string => {
  const explicit = trim(agent.iconKey)
  if (explicit.length > 0) {
    return explicit
  }
  // The live definition's icon, so an agent looks the same in the palette that added it and everywhere else
  const fromDefinition = trim(definitionOf(agent)?.iconKey)
  if (fromDefinition.length > 0) {
    return fromDefinition
  }
  return roleDefaultIcons[agent.role] ?? roleDefaultIcons.generic
}
